import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ChevronRight, ChevronLeft, Minus } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface SetScore {
  team1: string;
  team2: string;
}

interface ReportResultModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId?: string;
  openMatchV2Id?: string;
  matchType?: "casual" | "league" | "tournament" | "open_match";
  prefilledPlayers?: string[];
}

export default function ReportResultModal({
  open,
  onOpenChange,
  bookingId,
  openMatchV2Id,
  matchType = "casual",
  prefilledPlayers = [],
}: ReportResultModalProps) {
  // PR-AV: useTranslation() must be called at component scope.
  const { t } = useTranslation();
  const { user, profile: _profile } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();

  const [step, setStep] = useState(1);
  const [team1, setTeam1] = useState<string[]>([]);
  const [team2, setTeam2] = useState<string[]>([]);
  const [winner, setWinner] = useState<1 | 2 | null>(null);
  const [sets, setSets] = useState<SetScore[]>([{ team1: "", team2: "" }]);
  const [assessments, setAssessments] = useState<Record<string, number>>({});

  // Fetch players — from open_match_v2_participants or booking_players
  const { data: bookingPlayers = [] } = useQuery({
    queryKey: ["match-players-report", bookingId, openMatchV2Id],
    queryFn: async () => {
      let playerIds: string[] = [];

      if (openMatchV2Id) {
        const { data } = await supabase
          .from("open_match_v2_participants" as any)
          .select("user_id")
          .eq("match_id", openMatchV2Id)
          .is("left_at", null);
        playerIds = (data ?? []).map((p: any) => p.user_id);
      } else if (bookingId) {
        const { data } = await supabase
          .from("booking_players")
          .select("user_id")
          .eq("booking_id", bookingId)
          .eq("status", "accepted");
        playerIds = (data ?? []).map((p: any) => p.user_id);
      }

      if (prefilledPlayers.length > 0) {
        playerIds = [...new Set([...playerIds, ...prefilledPlayers])];
      }

      if (playerIds.length === 0) return [];
      const { data: profiles } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, level")
        .in("id", playerIds);
      return profiles ?? [];
    },
    enabled: (!!bookingId || !!openMatchV2Id || prefilledPlayers.length > 0) && open,
  });

  // Unassigned players
  const assignedIds = new Set([...team1, ...team2]);
  const unassigned = bookingPlayers.filter((p: any) => !assignedIds.has(p.id));

  const addToTeam = (playerId: string, team: 1 | 2) => {
    if (team === 1) setTeam1((prev) => [...prev, playerId]);
    else setTeam2((prev) => [...prev, playerId]);
  };

  const removeFromTeam = (playerId: string, team: 1 | 2) => {
    if (team === 1) setTeam1((prev) => prev.filter((id) => id !== playerId));
    else setTeam2((prev) => prev.filter((id) => id !== playerId));
  };

  const getPlayerName = (id: string) => bookingPlayers.find((p: any) => p.id === id)?.full_name ?? "?";
  const getPlayerLevel = (id: string) => bookingPlayers.find((p: any) => p.id === id)?.level ?? 5;

  const addSet = () => setSets([...sets, { team1: "", team2: "" }]);
  const removeSet = (i: number) => setSets(sets.filter((_, idx) => idx !== i));
  const updateSet = (i: number, field: "team1" | "team2", val: string) => {
    const updated = [...sets];
    updated[i] = { ...updated[i], [field]: val };
    setSets(updated);
  };

  const scoreString = sets.map((s) => `${s.team1}-${s.team2}`).join(", ");

  // Opponents to the current user for assessment
  const myTeam = team1.includes(user?.id ?? "") ? 1 : team2.includes(user?.id ?? "") ? 2 : null;
  const opponents = myTeam === 1 ? team2 : myTeam === 2 ? team1 : [];

  const adjustAssessment = (playerId: string, delta: number) => {
    const currentLevel = getPlayerLevel(playerId);
    const current = assessments[playerId] ?? currentLevel;
    const newVal = Math.max(1, Math.min(10, current + delta));
    setAssessments((prev) => ({ ...prev, [playerId]: newVal }));
  };

  const reportMutation = useMutation({
    mutationFn: async () => {
      if (!user || !organization) throw new Error("Ej inloggad");
      if (!winner) throw new Error("Välj vinnare");

      const winnerTeam = winner === 1 ? team1 : team2;
      const loserTeam = winner === 1 ? team2 : team1;

      if (sets.some((s) => !s.team1 || !s.team2)) {
        throw new Error("Fyll i alla set-resultat");
      }

      // Save match result
      const { error } = await supabase.from("match_results").insert({
        organization_id: organization.id,
        booking_id: bookingId || null,
        open_match_v2_id: openMatchV2Id || null,
        match_type: matchType,
        winner_team: winnerTeam,
        loser_team: loserTeam,
        score: scoreString,
        reported_by: user.id,
        confirmed: false,
      } as any);
      if (error) throw error;

      // Save level assessments
      for (const [assessedId, suggestedLevel] of Object.entries(assessments)) {
        await supabase.from("level_assessments").insert({
          assessed_user_id: assessedId,
          assessor_user_id: user.id,
          suggested_level: suggestedLevel,
        });
      }
    },
    onSuccess: () => {
      toast.success(t("booking:toast.resultReported"));
      qc.invalidateQueries({ queryKey: ["match-results"] });
      qc.invalidateQueries({ queryKey: ["player-stats"] });
      onOpenChange(false);
      resetForm();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const resetForm = () => {
    setStep(1);
    setTeam1([]);
    setTeam2([]);
    setWinner(null);
    setSets([{ team1: "", team2: "" }]);
    setAssessments({});
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Rapportera matchresultat</DialogTitle>
          <DialogDescription>
            {step === 1 && "Fördela spelare i lag"}
            {step === 2 && "Ange set-resultat"}
            {step === 3 && "Bedöm motståndarnas nivå (valfritt)"}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex gap-1 mb-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className={cn("h-1 flex-1 rounded-full", s <= step ? "bg-primary" : "bg-muted")} />
          ))}
        </div>

        {/* STEP 1: Team assignment */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Unassigned */}
            {unassigned.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Ej tilldelade</Label>
                <div className="space-y-1">
                  {unassigned.map((p: any) => (
                    <div key={p.id} className="flex items-center justify-between rounded-lg border p-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{p.full_name}</span>
                        <LevelBadge level={p.level ?? 1} size="sm" />
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => addToTeam(p.id, 1)} className="text-xs h-7 px-2">Lag 1</Button>
                        <Button size="sm" variant="outline" onClick={() => addToTeam(p.id, 2)} className="text-xs h-7 px-2">Lag 2</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Team 1 */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Lag 1</Label>
              <div className="rounded-lg border border-dashed p-2 min-h-[3rem] space-y-1">
                {team1.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">Dra spelare hit</p>}
                {team1.map((id) => (
                  <div key={id} className="flex items-center justify-between bg-muted/50 rounded p-1.5">
                    <span className="text-sm">{getPlayerName(id)}</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removeFromTeam(id, 1)}>×</Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Team 2 */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Lag 2</Label>
              <div className="rounded-lg border border-dashed p-2 min-h-[3rem] space-y-1">
                {team2.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">Dra spelare hit</p>}
                {team2.map((id) => (
                  <div key={id} className="flex items-center justify-between bg-muted/50 rounded p-1.5">
                    <span className="text-sm">{getPlayerName(id)}</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removeFromTeam(id, 2)}>×</Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Winner selection */}
            {team1.length > 0 && team2.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">Vem vann?</Label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={winner === 1 ? "default" : "outline"}
                    onClick={() => setWinner(1)}
                    className="flex-1"
                  >
                    Lag 1
                  </Button>
                  <Button
                    size="sm"
                    variant={winner === 2 ? "default" : "outline"}
                    onClick={() => setWinner(2)}
                    className="flex-1"
                  >
                    Lag 2
                  </Button>
                </div>
              </div>
            )}

            <Button
              className="w-full"
              disabled={team1.length === 0 || team2.length === 0 || !winner}
              onClick={() => setStep(2)}
            >
              Nästa <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        {/* STEP 2: Set scores */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="text-center text-sm text-muted-foreground mb-2">
              {team1.map(getPlayerName).join(" & ")} <span className="font-medium">vs</span> {team2.map(getPlayerName).join(" & ")}
            </div>

            <div className="space-y-2">
              {sets.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-10 shrink-0">Set {i + 1}</span>
                  <Input type="number" min={0} max={7}
                    value={s.team1}
                    onChange={(e) => updateSet(i, "team1", e.target.value)}
                    className="w-14 text-center h-9"
                    placeholder="0"
                  />
                  <span className="text-muted-foreground">–</span>
                  <Input type="number" min={0} max={7}
                    value={s.team2}
                    onChange={(e) => updateSet(i, "team2", e.target.value)}
                    className="w-14 text-center h-9"
                    placeholder="0"
                  />
                  {sets.length > 1 && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removeSet(i)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              {sets.length < 5 && (
                <Button variant="outline" size="sm" onClick={addSet} className="gap-1">
                  <Plus className="h-3.5 w-3.5" /> Lägg till set
                </Button>
              )}
            </div>

            <p className="text-sm text-muted-foreground text-center">
              Resultat: <strong>{scoreString || "—"}</strong>
            </p>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                <ChevronLeft className="h-4 w-4 mr-1" /> Tillbaka
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={sets.some((s) => !s.team1 || !s.team2)}
                className="flex-1"
              >
                Nästa <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Level assessment */}
        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Hur bedömer du dina motståndares nivå? (Valfritt)
            </p>

            {opponents.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">Inga motståndare att bedöma</p>
            ) : (
              <div className="space-y-3">
                {opponents.map((opId) => {
                  const currentLevel = getPlayerLevel(opId);
                  const assessed = assessments[opId] ?? currentLevel;
                  return (
                    <div key={opId} className="flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <p className="text-sm font-medium">{getPlayerName(opId)}</p>
                        <p className="text-xs text-muted-foreground">Nuvarande: {currentLevel}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="icon" variant="outline" className="h-8 w-8"
                          onClick={() => adjustAssessment(opId, -1)}
                          disabled={assessed <= 1}
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </Button>
                        <LevelBadge level={assessed} size="sm" />
                        <Button size="icon" variant="outline" className="h-8 w-8"
                          onClick={() => adjustAssessment(opId, 1)}
                          disabled={assessed >= 10}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                <ChevronLeft className="h-4 w-4 mr-1" /> Tillbaka
              </Button>
              <Button
                onClick={() => reportMutation.mutate()}
                disabled={reportMutation.isPending}
                className="flex-1"
              >
                {reportMutation.isPending ? <Loader2 className="animate-spin h-4 w-4" /> : "Rapportera"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
